import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { toast } from 'sonner'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/lib/api'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { EmptyState } from '@/components/shared/EmptyState'
import type { AccountPeriodUsage, DashboardData, PeriodUsageMetric } from '@/types'
import { formatUsagePeriodEnd, usageWarningMessage } from '@/lib/plan-usage'
import {
  ArrowUpRight, Globe, Mail, Send, Inbox, TrendingUp,
  AlertTriangle, ChevronRight, Zap
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid
} from 'recharts'

interface AnalyticsData {
  daily: { date: string; sent: number; delivered: number; bounced: number }[]
  by_domain: { domain: string; sent: number }[]
  by_recipient_domain: { domain: string; sent: number }[]
}

interface PlanSummary {
  slug: string
  name: string
  price_cents: number
}

// CHECKPOINT: PRD-10 FR-10.9 — Overview dashboard with usage cards, charts, and recent activity.
export function Overview() {
  const { user } = useAuth()
  const [data, setData] = useState<DashboardData | null>(null)
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [plans, setPlans] = useState<PlanSummary[]>([])
  const [periodUsage, setPeriodUsage] = useState<AccountPeriodUsage | null>(null)
  const [chartData, setChartData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        // CHECKPOINT: PRD-10 FR-10.9.2 — Load 30-day analytics for usage charts
        const today = new Date()
        const thirtyDaysAgo = new Date(today)
        thirtyDaysAgo.setDate(today.getDate() - 29)
        const date_from = thirtyDaysAgo.toISOString().split('T')[0]
        const date_to = today.toISOString().split('T')[0]

        // Try to fetch dashboard data; fall back to building from individual endpoints
        let dashboardData: DashboardData
        let analyticsRes: any = { data: { daily: [], by_domain: [], by_recipient_domain: [] } }
        let plansRes: any = { data: { plans: [] } }
        let usageRes: any = { data: null }
        try {
          const [dashRes, analytics, plansResponse, currentUsageResponse] = await Promise.all([
            api.get('/account/dashboard'),
            api
              .get('/account/analytics/sends', { params: { date_from, date_to } })
              .catch(() => ({ data: { daily: [], by_domain: [], by_recipient_domain: [] } })),
            api.get('/billing/plans').catch(() => ({ data: { plans: [] } })),
            // CHECKPOINT: PRD-14 FR-14.1.6 — Use the current metering period for customer-facing allowance cards.
            api.get('/account/usage').catch(() => ({ data: null })),
          ])
          dashboardData = dashRes.data
          analyticsRes = analytics
          plansRes = plansResponse
          usageRes = currentUsageResponse
        } catch {
          // Build dashboard from individual endpoints
          const [accountRes, planRes, domainsRes, mailboxesRes, sendsRes, inboxRes, analyticsFallback, plansFallback, usageFallback] = await Promise.all([
            api.get('/account/me').catch(() => ({ data: null })),
            api.get('/account/plan').catch(() => ({ data: null })),
            api.get('/domains').catch(() => ({ data: { domains: [] } })),
            api.get('/mailboxes').catch(() => ({ data: { mailboxes: [] } })),
            api.get('/outbound/messages?per_page=5').catch(() => ({ data: { messages: [] } })),
            api.get('/inbound/messages?per_page=5').catch(() => ({ data: { messages: [] } })),
            api
              .get('/account/analytics/sends', { params: { date_from, date_to } })
              .catch(() => ({ data: { daily: [], by_domain: [], by_recipient_domain: [] } })),
            api.get('/billing/plans').catch(() => ({ data: { plans: [] } })),
            api.get('/account/usage').catch(() => ({ data: null })),
          ])

          analyticsRes = analyticsFallback
          plansRes = plansFallback
          usageRes = usageFallback

          const account = accountRes.data
          const planData = planRes.data
          const domains = domainsRes.data.domains || []
          const mailboxes = mailboxesRes.data.mailboxes || []
          const sends = sendsRes.data.messages || []
          const inbound = inboxRes.data.messages || []

          const plan = account?.plan || ''
          const usage = account?.usage || {}
          const limits = planData?.limits || {}
          const domainLimit = usage.domains_limit || limits.domains || 0
          const mailboxLimit = usage.mailboxes_limit || limits.mailboxes || 0
          const emailsYearlyLimit = limits.emails_yearly || usage.emails_limit || 0
          const storageLimit = usage.storage_limit_mb || limits.storage_mb || 0

          // CHECKPOINT: PRD-10 FR-10.9.1 — Build fallback usage from individual endpoints
          dashboardData = {
            account: {
              id: account?.id || '',
              email: account?.email || '',
              display_name: account?.display_name || null,
              plan,
              status: account?.status || 'active',
              email_verified: account?.email_verified || false,
              timezone: account?.timezone || 'UTC',
              created_at: account?.created_at || '',
              current_period_end: account?.current_period_end,
            },
            usage: {
              domains: { used: usage.domains_used || domains.length, limit: domainLimit, percentage: domainLimit > 0 ? Math.min(100, Math.round(((usage.domains_used || domains.length) / domainLimit) * 100)) : 0 },
              mailboxes: { used: usage.mailboxes_used || mailboxes.length, limit: mailboxLimit, percentage: mailboxLimit > 0 ? Math.min(100, Math.round(((usage.mailboxes_used || mailboxes.length) / mailboxLimit) * 100)) : 0 },
              // Fallback uses the yearly count since plans bill yearly
              emails_sent_this_year: usage.emails_sent_this_year || 0,
              emails_limit: emailsYearlyLimit > 0 ? emailsYearlyLimit : 1,
              emails_percentage: emailsYearlyLimit > 0 ? Math.min(100, Math.round(((usage.emails_sent_this_year || 0) / emailsYearlyLimit) * 100)) : 0,
              storage_mb: { used: usage.storage_used_mb || 0, limit: storageLimit, percentage: storageLimit > 0 ? Math.min(100, Math.round(((usage.storage_used_mb || 0) / storageLimit) * 100)) : 0 },
            },
            recent_sends: sends,
            recent_inbound: inbound,
            alerts: account?.email_verified ? [] : [{ type: 'warning', message: 'Please verify your email address' }],
          }
        }
        setData(dashboardData)
        setAnalytics(analyticsRes.data)
        setPlans(plansRes.data.plans || [])
        setPeriodUsage(usageRes.data)

        const daily = analyticsRes.data?.daily || []
        // Map to last 30 days for the chart, filling missing days with 0
        const last30 = []
        for (let i = 29; i >= 0; i--) {
          const d = new Date()
          d.setDate(d.getDate() - i)
          const dateStr = d.toISOString().split('T')[0]
          const found = daily.find((x: any) => x.date === dateStr)
          last30.push({
            day: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            sent: found?.sent || 0,
            delivered: found?.delivered || 0,
            bounced: found?.bounced || 0,
          })
        }
        setChartData(last30)
      } catch {
        // CHECKPOINT: PRD-10 FR-10.13.4 — Show error state when dashboard data fails to load
        setError(true)
      } finally {
        setLoading(false)
      }
    }
    fetchDashboard()
  }, [])

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-accent rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-accent rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-[260px] bg-accent rounded-xl" />
          <div className="h-[260px] bg-accent rounded-xl" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-[200px] bg-accent rounded-xl" />
          <div className="h-[200px] bg-accent rounded-xl" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState
        title="Failed to load dashboard"
        description="We couldn't load your overview data. Please try refreshing the page."
        icon={<AlertTriangle className="h-5 w-5" />}
      />
    )
  }

  const usage = data?.usage

  // CHECKPOINT: PRD-10 FR-10.9.3 — 80% usage threshold upgrade prompt
  const planSlug = data?.account?.plan || ''
  const currentPlan = plans.find((p) => p.slug === planSlug)
  const upgradePlan = plans.find(
    (p) => p.slug !== planSlug && p.price_cents > (currentPlan?.price_cents || 0)
  )
  const usageEntries = usage
    ? [
        { key: 'Domains', pct: usage.domains.percentage },
        { key: 'Mailboxes', pct: usage.mailboxes.percentage },
        { key: 'Outbound deliveries', pct: periodUsage?.outbound.percent || 0 },
        { key: 'Inbound deliveries', pct: periodUsage?.inbound.percent || 0 },
        { key: 'Storage', pct: usage.storage_mb.percentage },
      ]
    : []
  const highestUsage = usageEntries.reduce(
    (max, u) => (u.pct > max.pct ? u : max),
    usageEntries[0] || { key: '', pct: 0 }
  )
  const showUpgradeBanner = Boolean(upgradePlan && highestUsage.pct >= 80)
  const hasConnectedDomain = (usage?.domains.used || 0) > 0
  // Keep a single domain CTA in the first-run state. The setup callout gives
  // the action its necessary context; the header action returns once there is
  // no longer a first-domain setup task to complete.
  const needsFirstDomain = user?.status === 'onboarding' && !hasConnectedDomain
  const domainActionLabel = hasConnectedDomain ? 'Manage domains' : 'Connect domain'

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="pt-page-header">
        <div>
          <p className="pt-eyebrow">Account</p>
          <h1 className="pt-page-title mt-2">Overview</h1>
          <p className="pt-page-description">
            Domains, mailboxes, and sending usage.
          </p>
        </div>
        {!needsFirstDomain && (
          <Link to="/domains">
            <Button size="sm" className="gap-2">
              <Globe className="mr-2 h-4 w-4" />
              {domainActionLabel}
            </Button>
          </Link>
        )}
      </div>

      {/* Email Verification Banner */}
      {user && !user.email_verified && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-blue-500/20 bg-blue-500/8 px-5 py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 text-blue-400 shrink-0" />
            <p className="text-sm text-blue-300">
              Verify your email address to finish setting up your account.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              setVerifying(true)
              try {
                await api.post('/auth/resend-verification', { email: user.email })
                toast.success('Verification email sent! Check your inbox.')
              } catch {
                toast.error('Failed to send verification email')
              } finally {
                setVerifying(false)
              }
            }}
            disabled={verifying}
          >
            {verifying ? 'Sending...' : 'Resend Email'}
          </Button>
        </div>
      )}

      {/* CHECKPOINT: PRD-14 FR-14.1.1 — New account onboarding remains usable on the database-backed Free plan. */}
      {needsFirstDomain && (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/25 bg-primary/10 px-5 py-5">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-semibold text-foreground">
                Add your first mailbox.
              </p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                Connect and verify a domain first.
              </p>
            </div>
          </div>
          <Link to="/domains">
            <Button size="sm">
              <Globe className="mr-1.5 h-3.5 w-3.5" />
              Connect domain
            </Button>
          </Link>
        </div>
      )}

      {/* Upgrade Banner */}
      {showUpgradeBanner && upgradePlan && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-amber-400 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-200">
                You're at {highestUsage.pct}% of your {highestUsage.key.toLowerCase()} limit.
              </p>
              <p className="text-xs text-amber-300/70">
                Upgrade to {upgradePlan.name} for more {highestUsage.key.toLowerCase()} and additional features.
              </p>
            </div>
          </div>
          <Link to="/billing">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
              <Zap className="mr-1.5 h-3.5 w-3.5" />
              Upgrade
            </Button>
          </Link>
        </div>
      )}

      {/* Usage Cards */}
      <section aria-labelledby="workspace-usage-heading">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <p className="pt-eyebrow">Account limits</p>
            <h2 id="workspace-usage-heading" className="mt-1 text-base font-semibold tracking-[-0.015em]">Plan usage</h2>
          </div>
          <Link to="/billing" className="hidden items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 sm:inline-flex">Manage plan <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <UsageCard
          icon={<Globe className="h-4 w-4 text-violet-400" />}
          label="Domains"
          used={usage?.domains.used || 0}
          limit={usage?.domains.limit || 0}
          percentage={usage?.domains.percentage || 0}
          href="/domains"
        />
        <UsageCard
          icon={<Mail className="h-4 w-4 text-blue-400" />}
          label="Mailboxes"
          used={usage?.mailboxes.used || 0}
          limit={usage?.mailboxes.limit || 0}
          percentage={usage?.mailboxes.percentage || 0}
          href="/mailboxes"
        />
        <UsageCard
          icon={<Zap className="h-4 w-4 text-amber-400" />}
          label="Storage"
          used={usage?.storage_mb.used || 0}
          limit={usage?.storage_mb.limit || 1}
          percentage={usage?.storage_mb.percentage || 0}
          href="/billing"
          unit="MB"
        />
      </div>
      </section>

      {/* CHECKPOINT: PRD-14 FR-14.1.6/FR-14.6.2 — Current outbound and inbound allowance, reset, and remediation path. */}
      {periodUsage ? (
        <section aria-labelledby="delivery-allowance-heading" className="pt-surface rounded-xl px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="pt-eyebrow">Sending</p>
              <h2 id="delivery-allowance-heading" className="mt-1 text-base font-semibold">Usage this period</h2>
              <p className="text-sm text-muted-foreground">
                {periodUsage.plan.name} plan · resets {formatUsagePeriodEnd(periodUsage.period.end)}
              </p>
            </div>
            <Link to="/billing" className="text-sm font-medium text-primary hover:underline">View plan details</Link>
          </div>
          <div className="mt-5 grid gap-x-8 gap-y-5 md:grid-cols-2">
            <PeriodUsageRow label="Outbound deliveries" metric={periodUsage.outbound} direction="outbound" />
            <PeriodUsageRow label="Inbound deliveries" metric={periodUsage.inbound} direction="inbound" />
          </div>
        </section>
      ) : (
        <section className="pt-surface rounded-xl px-5 py-5" aria-live="polite">
          <h2 className="text-base font-semibold">Sending usage</h2>
          <p className="mt-1 text-sm text-muted-foreground">Inbound and outbound usage is unavailable. Refresh to try again.</p>
        </section>
      )}

      {/* Charts */}
      <section aria-label="Sending and delivery activity" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Emails Sent (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Bar dataKey="sent" fill="url(#sentGradient)" radius={[4, 4, 0, 0]} />
                  <defs>
                    <linearGradient id="sentGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" />
                      <stop offset="100%" stopColor="#7c3aed" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Delivery Rate (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Line type="monotone" dataKey="delivered" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="sent" stroke="#8b5cf6" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* CHECKPOINT: PRD-10 FR-10.9.2 — Top sending and recipient domain charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Top Sending Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              {analytics && analytics.by_domain.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...analytics.by_domain.slice(0, 5)].reverse()}
                    layout="vertical"
                    margin={{ left: 16, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis type="category" dataKey="domain" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" width={120} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Bar dataKey="sent" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground h-full flex items-center justify-center">
                  No sending activity in the last 30 days
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Top Recipient Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              {analytics && analytics.by_recipient_domain.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...analytics.by_recipient_domain.slice(0, 5)].reverse()}
                    layout="vertical"
                    margin={{ left: 16, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis type="category" dataKey="domain" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" width={120} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Bar dataKey="sent" fill="#22c55e" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground h-full flex items-center justify-center">
                  No recipient activity in the last 30 days
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Recent Sends</CardTitle>
            <Link to="/sending" className="text-xs text-primary hover:underline flex items-center">
              View all <ChevronRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {data?.recent_sends && data.recent_sends.length > 0 ? (
              <div className="space-y-2">
                {data.recent_sends.map((msg) => (
                  <div key={msg.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{msg.subject}</p>
                      <p className="text-xs text-muted-foreground">To: {msg.to?.map((r: any) => typeof r === 'string' ? r : r.email || r).join(', ')}</p>
                    </div>
                    <StatusBadge status={msg.status} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No emails sent yet"
                description="Send from the dashboard or API to see activity here."
                icon={<Send className="h-5 w-5" />}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Recent Inbound</CardTitle>
            <Link to="/inbox" className="text-xs text-primary hover:underline flex items-center">
              View all <ChevronRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {data?.recent_inbound && data.recent_inbound.length > 0 ? (
              <div className="space-y-2">
                {data.recent_inbound.map((msg) => (
                  <div key={msg.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{msg.subject || '(no subject)'}</p>
                      <p className="text-xs text-muted-foreground">From: {msg.from_addr?.email}</p>
                    </div>
                    {!msg.is_read && <span className="h-2 w-2 rounded-full bg-blue-400" />}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No inbound emails yet"
                description="Mail sent to your domains appears here."
                icon={<Inbox className="h-5 w-5" />}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function UsageCard({ icon, label, used, limit, percentage, href, unit = '' }: {
  icon: React.ReactNode
  label: string
  used: number
  limit: number
  percentage: number
  href: string
  unit?: string
}) {
  const isUnlimited = limit < 0
  return (
    <Link to={href} className="block">
      <Card className="group h-full border-border/70 transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:bg-accent/35">
        <CardContent className="p-5">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                {icon}
              </div>
              <span className="text-sm font-medium text-muted-foreground">{label}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          </div>
          <div className="mb-3 flex items-baseline gap-1">
            <span className="text-[1.75rem] font-semibold tracking-[-0.045em]">{used}</span>
            {!isUnlimited && (
              <span className="text-sm text-muted-foreground">/ {limit}{unit}</span>
            )}
            {isUnlimited && <span className="text-sm text-muted-foreground">unlimited</span>}
          </div>
          {!isUnlimited && (
            <div className="space-y-1">
              <Progress value={percentage} className="h-1.5" />
              <p className="text-[11px] text-muted-foreground">{percentage}% of plan capacity used</p>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}

function PeriodUsageRow({ label, metric, direction }: {
  label: string
  metric: PeriodUsageMetric
  direction: 'inbound' | 'outbound'
}) {
  const warning = usageWarningMessage(metric, direction)
  const isUnlimited = metric.limit < 0

  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium">{label}</p>
        <p className="shrink-0 text-sm tabular-nums text-muted-foreground">
          {metric.used.toLocaleString()}
          {isUnlimited ? ' used' : ` of ${metric.limit.toLocaleString()}`}
        </p>
      </div>
      {!isUnlimited && <Progress value={metric.percent} className="mt-2 h-1.5" />}
      <p className={`mt-2 text-xs ${warning ? 'text-amber-500' : 'text-muted-foreground'}`}>
        {warning || `${metric.remaining.toLocaleString()} remaining this period.`}
      </p>
    </div>
  )
}
